import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  SetStateAction
} from "react";
import { Icon } from "@tvg/design-system";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";
import { fontNormal } from "@tvg/atomic-ui/_static/Typography";
import { useBraintreeHostedFields } from "@tvg/sh-lib-paws/hooks/useBraintreeHostedFields";
import { creditDebitCardCreation } from "@tvg/sh-lib-paws/redux/defaultValues";
import { HostedFieldState } from "@tvg/sh-lib-paws/types/braintree";
import { renderError } from "../FundsModal/common/InputField/methods";
import { ErrorMessage } from "../../types";
import SpinningWheel from "../SpinningWheel";
import {
  Container,
  FieldGroup,
  Label,
  FieldContainer,
  CvvIconContainer,
  Row,
  Col,
  LoadingContainer
} from "./styled-components";

export interface BraintreeHostedFieldsRef {
  tokenize: () => Promise<{
    nonce: string;
    details: { cardType: string; lastFour: string };
  }>;
  isReady: boolean;
  deviceData: string;
  allFieldsValid: boolean;
}

interface BraintreeHostedFieldsProps {
  isModalOpen: boolean;
  setErrorMessage: React.Dispatch<SetStateAction<ErrorMessage>>;
  onFieldFocus?: (fieldName: string) => void;
  onFieldBlur?: (fieldName: string) => void;
  onFieldsValidityChange?: (allValid: boolean) => void;
  onCvvIconClick?: () => void;
}

const BraintreeHostedFields = forwardRef<
  BraintreeHostedFieldsRef,
  BraintreeHostedFieldsProps
>(
  (
    {
      isModalOpen,
      setErrorMessage,
      onFieldFocus,
      onFieldBlur,
      onFieldsValidityChange,
      onCvvIconClick
    },
    ref
  ) => {
    const hostedFieldStyles = useMemo(
      () => ({
        input: {
          "font-size": "14px",
          "font-family": fontNormal,
          "font-weight": "400",
          color: buildColor("grey", "900"),
          "line-height": "22px"
        },
        "input.invalid": {
          color: buildColor("orange", "600")
        },
        "input.valid": {
          color: buildColor("grey", "900")
        },
        "input::placeholder": {
          color: buildColor("grey", "700")
        }
      }),
      []
    );

    const [visitedFields, setVisitedFields] = useState<Set<string>>(new Set());

    const handleFieldBlur = useCallback(
      (fieldName: string) => {
        setVisitedFields((prev) => {
          if (prev.has(fieldName)) return prev;
          const next = new Set(prev);
          next.add(fieldName);
          return next;
        });
        onFieldBlur?.(fieldName);
      },
      [onFieldBlur]
    );

    const {
      isReady,
      isLoading,
      fieldStates,
      deviceData,
      tokenize,
      containerRef
    } = useBraintreeHostedFields({
      isModalOpen,
      styles: hostedFieldStyles,
      setErrorMessage,
      onFieldFocus,
      onFieldBlur: handleFieldBlur
    });

    const allFieldsValid =
      fieldStates.number.isValid &&
      fieldStates.expirationDate.isValid &&
      fieldStates.cvv.isValid;

    useEffect(() => {
      onFieldsValidityChange?.(allFieldsValid);
    }, [allFieldsValid, onFieldsValidityChange]);

    useImperativeHandle(
      ref,
      () => ({
        tokenize,
        isReady,
        deviceData,
        allFieldsValid
      }),
      [tokenize, isReady, deviceData, allFieldsValid]
    );

    const { cardNumber, expirationDateCard, cvv } = creditDebitCardCreation;

    const getFieldError = (
      field: HostedFieldState,
      fieldName: string,
      requiredMsg: string,
      invalidMsg: string
    ): string | undefined => {
      if (!visitedFields.has(fieldName) || field.isFocused) return undefined;
      if (field.isEmpty) return requiredMsg;
      if (!field.isValid) return invalidMsg;
      return undefined;
    };

    const numberError = getFieldError(
      fieldStates.number,
      "number",
      cardNumber.validations.required,
      cardNumber.validations.invalid
    );
    const expirationError = getFieldError(
      fieldStates.expirationDate,
      "expirationDate",
      expirationDateCard.validations.required,
      expirationDateCard.validations.invalidDate
    );
    const cvvError = getFieldError(
      fieldStates.cvv,
      "cvv",
      cvv.validations.required,
      cvv.validations.invalid
    );

    return (
      <Container data-qa-label="braintree-hosted-fields">
        {(isLoading || !isReady) && (
          <LoadingContainer data-qa-label="braintree-hosted-fields-loading">
            <SpinningWheel />
          </LoadingContainer>
        )}
        <FieldGroup isHidden={!isReady}>
          <Label
            id="label-braintree-card-number"
            data-qa-label="label-braintree-card-number"
          >
            Card Number
          </Label>
          <FieldContainer
            ref={containerRef}
            id="braintree-card-number"
            role="group"
            aria-labelledby="label-braintree-card-number"
            data-qa-label="braintree-card-number"
            isFocused={fieldStates.number.isFocused}
            hasError={!!numberError}
          />
          {numberError && renderError(numberError, "braintree-card-number")}
        </FieldGroup>

        <Row isHidden={!isReady}>
          <Col>
            <FieldGroup isHidden={!isReady}>
              <Label
                id="label-braintree-expiration-date"
                data-qa-label="label-braintree-expiration-date"
              >
                Expiration Date
              </Label>
              <FieldContainer
                id="braintree-expiration-date"
                role="group"
                aria-labelledby="label-braintree-expiration-date"
                data-qa-label="braintree-expiration-date"
                isFocused={fieldStates.expirationDate.isFocused}
                hasError={!!expirationError}
              />
              {expirationError &&
                renderError(expirationError, "braintree-expiration-date")}
            </FieldGroup>
          </Col>
          <Col>
            <FieldGroup isHidden={!isReady}>
              <Label
                id="label-braintree-cvv"
                data-qa-label="label-braintree-cvv"
              >
                CVV
              </Label>
              <FieldContainer
                id="braintree-cvv"
                role="group"
                aria-labelledby="label-braintree-cvv"
                data-qa-label="braintree-cvv"
                isFocused={fieldStates.cvv.isFocused}
                hasError={!!cvvError}
              >
                {isReady && onCvvIconClick && (
                  <CvvIconContainer
                    data-qa-label="braintree-cvv-icon"
                    role="button"
                    tabIndex={0}
                    aria-label="CVV information"
                    onMouseDown={(event) => event.preventDefault()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onCvvIconClick();
                      }
                    }}
                    onClick={onCvvIconClick}
                  >
                    <Icon
                      name="cvv"
                      size="xl"
                      lineColor={buildColor("blue_accent", "500")}
                    />
                  </CvvIconContainer>
                )}
              </FieldContainer>
              {cvvError && renderError(cvvError, "braintree-cvv")}
            </FieldGroup>
          </Col>
        </Row>
      </Container>
    );
  }
);

BraintreeHostedFields.displayName = "BraintreeHostedFields";

export default BraintreeHostedFields;
